/**
 * Registration Draft Utility
 * Saves and restores registration progress to prevent data loss
 */

const DRAFT_PREFIX = 'eaquahealth_draft_';
const DRAFT_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Save registration draft to localStorage
 * @param {string} formType - Type of registration (doctor, vendor, lab, user)
 * @param {object} data - Form data to save
 * @param {number} currentStep - Current step in multi-step form
 */
export const saveDraft = (formType, data, currentStep = 1) => {
    try {
        const draftKey = `${DRAFT_PREFIX}${formType}`;
        
        // Don't save sensitive data like passwords
        const safeData = { ...data };
        delete safeData.password;
        delete safeData.confirmPassword;
        
        // Don't save file objects (they can't be serialized)
        // Instead, save file names for reference
        Object.keys(safeData).forEach(key => {
            if (safeData[key] instanceof File) {
                safeData[`${key}_draft_name`] = safeData[key].name;
                delete safeData[key];
            }
        });
        
        const draft = {
            data: safeData,
            currentStep,
            savedAt: Date.now(),
            expiresAt: Date.now() + DRAFT_EXPIRY
        };
        
        localStorage.setItem(draftKey, JSON.stringify(draft));
        return true;
    } catch (error) {
        console.error('Failed to save draft:', error);
        return false;
    }
};

/**
 * Load registration draft from localStorage
 * @param {string} formType - Type of registration
 * @returns {object|null} - Saved draft data or null
 */
export const loadDraft = (formType) => {
    try {
        const draftKey = `${DRAFT_PREFIX}${formType}`;
        const draftStr = localStorage.getItem(draftKey);
        
        if (!draftStr) return null;
        
        const draft = JSON.parse(draftStr);
        
        // Check if draft has expired
        if (draft.expiresAt && Date.now() > draft.expiresAt) {
            clearDraft(formType);
            return null;
        }
        
        return draft;
    } catch (error) {
        console.error('Failed to load draft:', error);
        return null;
    }
};

/**
 * Clear registration draft
 * @param {string} formType - Type of registration
 */
export const clearDraft = (formType) => {
    try {
        const draftKey = `${DRAFT_PREFIX}${formType}`;
        localStorage.removeItem(draftKey);
        return true;
    } catch (error) {
        console.error('Failed to clear draft:', error);
        return false;
    }
};

/**
 * Check if a draft exists for the given form type
 * @param {string} formType - Type of registration
 * @returns {boolean}
 */
export const hasDraft = (formType) => {
    const draft = loadDraft(formType);
    return draft !== null;
};

/**
 * Get time since draft was saved
 * @param {string} formType - Type of registration
 * @returns {string} - Human readable time string
 */
export const getDraftAge = (formType) => {
    const draft = loadDraft(formType);
    if (!draft) return null;
    
    const ageMs = Date.now() - draft.savedAt;
    const minutes = Math.floor(ageMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
};

/**
 * Auto-save with debounce
 * Returns a debounced save function
 * @param {string} formType - Type of registration
 * @param {number} delay - Debounce delay in ms (default 2000)
 */
export const createAutoSave = (formType, delay = 2000) => {
    let timeoutId = null;
    
    return (data, currentStep) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        
        timeoutId = setTimeout(() => {
            saveDraft(formType, data, currentStep);
        }, delay);
    };
};

const registrationDraftUtils = {
    saveDraft,
    loadDraft,
    clearDraft,
    hasDraft,
    getDraftAge,
    createAutoSave
};

export default registrationDraftUtils;
